// Data for the Usage tab's spend chart, stacked by "Doing what". No React and
// no api client at runtime, so `node --test` covers it.
//
// The credit ledger is the source of truth for how much was charged; the
// Boo's own usage series only says what the model spend was FOR. So every
// bar is a ledger figure, and the brain's per-purpose costs are used purely
// as proportions to split the ledger's model charges (`ai`). Splits are done
// in whole micro-dollars (the ledger's precision) with largest-remainder
// rounding, so a bar's purpose segments add up to the ledger's `ai` exactly
// and the whole bar to the ledger's `total`.

import type { ContainerUsageByDay, UsageSeriesPoint } from "@/lib/api";
import { spendMonthKeys } from "./spend-range.ts";
import {
  LEDGER_SEGMENTS,
  OTHER_ACTIVITY,
  purposeColor,
  purposeHasColor,
  purposeLabel,
  type LedgerSegmentKey,
} from "./usage-purpose.ts";

export type LedgerDay = ContainerUsageByDay["days"][number];
export type Grain = "hour" | "day" | "month";

export interface Segment {
  /** dataKey on the chart rows (opaque: purpose keys aren't safe dataKeys). */
  id: string;
  label: string;
  color: string;
  /** Set for a purpose's own segment: clicking it toggles that purpose. */
  purpose?: string;
}

export interface ChartRow {
  /** 'YYYY-MM-DDTHH' (hour), 'YYYY-MM-DD' (day) or 'YYYY-MM' (month), UTC. */
  bucket: string;
  /** The bar's height: the ledger's total for the bucket when the bar is the
   * whole ledger bucket, else the sum of the segments shown. */
  total: number;
  [segmentId: string]: number | string;
}

export interface StackedChart {
  grain: Grain;
  rows: ChartRow[];
  /** Bottom to top; only segments with a nonzero value somewhere. */
  segments: Segment[];
}

/** Purposes with a hue of their own get a segment, ranked by window spend;
 * the rest fold into "Other activity". */
export const CHART_TOP_PURPOSES = 5;

const MICROS = 1_000_000;
const HOUR_MS = 60 * 60 * 1000;
const OTHER_ID = "other";
const LEDGER_KEYS: readonly LedgerSegmentKey[] = ["infrastructure", "services", "base"];

export function bucketKeyOf(ts: number, grain: Grain): string {
  const iso = new Date(ts).toISOString();
  return grain === "hour" ? iso.slice(0, 13) : grain === "day" ? iso.slice(0, 10) : iso.slice(0, 7);
}

/**
 * Splits `target` dollars in proportion to `weights`, in whole micro-dollars
 * with largest-remainder rounding: the parts sum to `target` rounded to the
 * micro-dollar, exactly. Non-positive weights get nothing; all-zero weights
 * get all zeros.
 */
export function allocate(target: number, weights: readonly number[]): number[] {
  const positive = weights.map((w) => (w > 0 ? w : 0));
  const weightSum = positive.reduce((a, w) => a + w, 0);
  if (weightSum <= 0) return weights.map(() => 0);
  const totalMicros = Math.round(target * MICROS);
  const sign = totalMicros < 0 ? -1 : 1;
  const abs = Math.abs(totalMicros);
  const exact = positive.map((w) => (w / weightSum) * abs);
  const parts = exact.map((e) => Math.floor(e));
  let rest = abs - parts.reduce((a, p) => a + p, 0);
  const order = exact
    .map((e, i) => ({ frac: e - parts[i], i }))
    .filter(({ i }) => positive[i] > 0)
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (let k = 0; rest > 0; k = (k + 1) % order.length, rest -= 1) parts[order[k].i] += 1;
  return parts.map((p) => (sign * p) / MICROS);
}

/** Every UTC day from `start`'s through `end`'s, inclusive: the ledger buckets
 * by UTC day, and a window ending today has charges dated today. */
export function dayKeysInclusive(start: Date, end: Date): string[] {
  const keys: string[] = [];
  const last = end.toISOString().slice(0, 10);
  const d = new Date(start.toISOString().slice(0, 10) + "T00:00:00Z");
  for (let key = d.toISOString().slice(0, 10); key <= last; key = d.toISOString().slice(0, 10)) {
    keys.push(key);
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return keys;
}

/** Every UTC hour bucket from `start`'s through `end`'s, inclusive. */
export function hourKeysInclusive(start: Date, end: Date): string[] {
  const keys: string[] = [];
  const last = Math.floor(end.getTime() / HOUR_MS) * HOUR_MS;
  for (let ts = Math.floor(start.getTime() / HOUR_MS) * HOUR_MS; ts <= last; ts += HOUR_MS) {
    keys.push(bucketKeyOf(ts, "hour"));
  }
  return keys;
}

/** The x-axis: the window's buckets plus any bucket the data has outside it,
 * so no charge is ever dropped off the edge. */
export function bucketKeys(
  grain: Grain,
  window: { start: Date; end: Date } | null,
  present: Iterable<string>,
): string[] {
  const keys = new Set<string>(
    grain === "month"
      ? spendMonthKeys([...present])
      : window === null
        ? []
        : grain === "day"
          ? dayKeysInclusive(window.start, window.end)
          : hourKeysInclusive(window.start, window.end),
  );
  for (const k of present) keys.add(k);
  return [...keys].sort();
}

// Brain cost per bucket per purpose ("" = no purpose recorded).
function groupPoints(points: readonly UsageSeriesPoint[], grain: Grain) {
  const out = new Map<string, Map<string, number>>();
  for (const p of points) {
    const bucket = bucketKeyOf(p.bucketTs, grain);
    const byPurpose = out.get(bucket) ?? new Map<string, number>();
    const key = p.key ?? "";
    byPurpose.set(key, (byPurpose.get(key) ?? 0) + p.costUsd);
    out.set(bucket, byPurpose);
  }
  return out;
}

// Segment layout shared by both builders: top colored purposes (or just the
// selected one), then "Other activity".
function purposeSegments(points: readonly UsageSeriesPoint[], selected: string | undefined) {
  const own = new Map<string, Segment>();
  const other: Segment = { id: OTHER_ID, ...OTHER_ACTIVITY };
  if (selected !== undefined) {
    own.set(selected, {
      id: "p0",
      label: purposeLabel(selected),
      color: purposeColor(selected),
      purpose: selected,
    });
  } else {
    const byPurpose = new Map<string, number>();
    for (const p of points) {
      if (p.key === null || !purposeHasColor(p.key)) continue;
      byPurpose.set(p.key, (byPurpose.get(p.key) ?? 0) + p.costUsd);
    }
    [...byPurpose.entries()]
      .filter(([, cost]) => cost > 0)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, CHART_TOP_PURPOSES)
      .forEach(([purpose], i) =>
        own.set(purpose, {
          id: `p${i}`,
          label: purposeLabel(purpose),
          color: purposeColor(purpose),
          purpose,
        }),
      );
  }
  // With a purpose selected, everything else is hidden, not folded.
  const segmentFor = (purpose: string): Segment | undefined =>
    own.get(purpose) ?? (selected === undefined ? other : undefined);
  return { ordered: [...own.values(), other], segmentFor, other };
}

function add(row: ChartRow, id: string, amount: number) {
  if (amount === 0) return;
  row[id] = ((row[id] as number | undefined) ?? 0) + amount;
}

function presentSegments(rows: readonly ChartRow[], ordered: readonly Segment[]): Segment[] {
  return ordered.filter((s) => rows.some((r) => typeof r[s.id] === "number" && r[s.id] !== 0));
}

/**
 * Day or month buckets, reconciled bucket by bucket to the ledger: each
 * bucket's `ai` is split across purposes by the brain's proportions for that
 * bucket (or shown whole as "Other activity" when the brain has nothing
 * there), and infrastructure, add-on services and base fee come straight from
 * the ledger. With `selected`, only that purpose's share is shown.
 */
export function buildLedgerChart(opts: {
  grain: "day" | "month";
  window: { start: Date; end: Date } | null;
  days: readonly LedgerDay[];
  points: readonly UsageSeriesPoint[];
  selected?: string;
}): StackedChart {
  const { grain, days, points, selected } = opts;
  const brain = groupPoints(points, grain);
  const ledger = new Map(days.map((d) => [d.day, d]));
  const { ordered, segmentFor, other } = purposeSegments(points, selected);
  const ledgerSegments: Segment[] = LEDGER_KEYS.map((k) => ({ id: k, ...LEDGER_SEGMENTS[k] }));

  const rows = bucketKeys(grain, opts.window, ledger.keys()).map((bucket) => {
    const d = ledger.get(bucket);
    const row: ChartRow = { bucket, total: 0 };
    if (d === undefined) return row;
    const entries = [...(brain.get(bucket) ?? new Map<string, number>()).entries()];
    const parts = allocate(d.ai, entries.map(([, cost]) => cost));
    if (parts.every((p) => p === 0)) {
      if (selected === undefined) add(row, other.id, d.ai);
    } else {
      entries.forEach(([purpose], i) => {
        const seg = segmentFor(purpose);
        if (seg !== undefined) add(row, seg.id, parts[i]);
      });
    }
    if (selected === undefined) {
      for (const k of LEDGER_KEYS) add(row, k, d[k]);
      row.total = d.total;
    } else {
      row.total = (row.p0 as number | undefined) ?? 0;
    }
    return row;
  });
  return { grain, rows, segments: presentSegments(rows, [...ordered, ...ledgerSegments]) };
}

/**
 * Brain buckets scaled onto one window figure: `target` is split across every
 * (bucket, purpose) cell in proportion to the brain's cost, so the bars add up
 * to `target` exactly. Used where the ledger has no matching bucket (hours)
 * or no matching dimension (a person, channel or conversation). Model spend
 * only: no infrastructure or add-on segments.
 */
export function buildScaledChart(opts: {
  grain: Grain;
  window: { start: Date; end: Date } | null;
  points: readonly UsageSeriesPoint[];
  target: number;
  selected?: string;
}): StackedChart {
  const { grain, points, target, selected } = opts;
  const brain = groupPoints(points, grain);
  const { ordered, segmentFor } = purposeSegments(points, selected);
  const cells: Array<{ bucket: string; purpose: string; cost: number }> = [];
  for (const [bucket, byPurpose] of brain) {
    for (const [purpose, cost] of byPurpose) cells.push({ bucket, purpose, cost });
  }
  const parts = allocate(target, cells.map((c) => c.cost));
  const rows = new Map<string, ChartRow>(
    bucketKeys(grain, opts.window, brain.keys()).map((bucket) => [bucket, { bucket, total: 0 }]),
  );
  cells.forEach((cell, i) => {
    const seg = segmentFor(cell.purpose);
    const row = rows.get(cell.bucket);
    if (seg === undefined || row === undefined) return;
    add(row, seg.id, parts[i]);
    row.total += parts[i];
  });
  const list = [...rows.values()];
  return { grain, rows: list, segments: presentSegments(list, ordered) };
}

/** The window's brain total, for the brain → ledger ratio. */
export function seriesTotal(points: readonly UsageSeriesPoint[]): number {
  return points.reduce((a, p) => a + p.costUsd, 0);
}
